import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { coursesApi } from "../lib";
import { showSuccess, showError } from "../lib/toast";
import {
  ArrowLeft,
  Save,
  Upload,
  X,
  Plus,
  Trash2,
  Image,
  ImagePlus,
  Video,
  DollarSign,
  Tag,
  BookOpen,
  Layout,
  CheckCircle,
  Loader,
  Eye,
} from "lucide-react";

interface FormData {
  title: string;
  shortDescription: string;
  description: string;
  category: string;
  subCategory: string;
  difficulty: string;
  language: string;
  price: number;
  originalPrice: number;
  discount: number;
  currency: string;
  thumbnailUrl: string;
  promoVideo: string;
  whatYouWillLearn: string[];
  requirements: string[];
  prerequisites: string;
  tags: string[];
  mobileAccess: boolean;
  isPublished: boolean;
  isFeatured: boolean;
}

const defaultFormData: FormData = {
  title: "",
  shortDescription: "",
  description: "",
  category: "",
  subCategory: "",
  difficulty: "beginner",
  language: "English",
  price: 0,
  originalPrice: 0,
  discount: 0,
  currency: "XAF",
  thumbnailUrl: "",
  promoVideo: "",
  whatYouWillLearn: [""],
  requirements: [""],
  prerequisites: "",
  tags: [],
  mobileAccess: true,
  isPublished: false,
  isFeatured: false,
};

const categories = [
  "Web Development",
  "Mobile Development",
  "Data Science",
  "Machine Learning",
  "DevOps",
  "Design",
  "Business",
  "Marketing",
  "Photography",
  "Music",
];

const difficulties = ["beginner", "intermediate", "advanced"];
const languages = [
  "English",
  "French",
  "Spanish",
  "German",
  "Chinese",
  "Portuguese",
];

const parseArrayField = (field: unknown): string[] => {
  if (!field) return [""];
  if (Array.isArray(field) && field.length > 0) {
    return field.filter((item) => item && item.trim());
  }
  if (typeof field === "string" && field.trim()) {
    try {
      const parsed = JSON.parse(field);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((item) => item && item.trim());
      }
    } catch {
      if (field.includes("\n")) {
        const lines = field.split("\n").filter((line) => line.trim());
        if (lines.length > 0) return lines;
      }
      return [field.trim()];
    }
  }
  return [""];
};

export default function UpdateCoursePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [tagInput, setTagInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleThumbnailUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showError("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError("Image must be less than 5MB");
      return;
    }
    setUploadingThumbnail(true);
    try {
      const response = await coursesApi.uploadThumbnail(file);
      if (response.success && response.data?.url) {
        updateField("thumbnailUrl", response.data.url);
        showSuccess("Thumbnail uploaded successfully");
      }
    } catch (err: any) {
      showError(err.message || "Failed to upload thumbnail");
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleThumbnailUrlChange = (url: string) => {
    updateField("thumbnailUrl", url);
  };

  useEffect(() => {
    const fetchCourse = async () => {
      if (!id) return;
      try {
        const response = await coursesApi.getCourseById(id);
        if (response.success && response.data) {
          const course = response.data;
          setFormData({
            title: course.title || "",
            shortDescription: course.shortDescription || "",
            description: course.description || "",
            category: course.category || "",
            subCategory: course.subCategory || "",
            difficulty: course.difficulty || "beginner",
            language: course.language || "English",
            price: course.price || 0,
            originalPrice: course.originalPrice || 0,
            discount: course.discount || 0,
            currency: course.currency || "XAF",
            thumbnailUrl: course.thumbnail || course.thumbnailUrl || "",
            promoVideo: course.promoVideo || "",
            whatYouWillLearn: parseArrayField(course.whatYouWillLearn),
            requirements: parseArrayField(course.requirements),
            prerequisites: course.prerequisites || "",
            tags: Array.isArray(course.tags) ? course.tags : [],
            mobileAccess: course.mobileAccess ?? true,
            isPublished: course.isPublished || false,
            isFeatured: course.isFeatured || false,
          });
        }
      } catch (err) {
        showError("Failed to load course");
        navigate("/dashboard/instructor/courses");
      } finally {
        setFetching(false);
      }
    };
    fetchCourse();
  }, [id]);

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayField = (
    field: "whatYouWillLearn" | "requirements",
    index: number,
    value: string,
  ) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    updateField(field, newArray);
  };

  const addArrayItem = (field: "whatYouWillLearn" | "requirements") => {
    updateField(field, [...formData[field], ""]);
  };

  const removeArrayItem = (
    field: "whatYouWillLearn" | "requirements",
    index: number,
  ) => {
    if (formData[field].length > 1) {
      const newArray = formData[field].filter((_, i) => i !== index);
      updateField(field, newArray);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      updateField("tags", [...formData.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    updateField(
      "tags",
      formData.tags.filter((t) => t !== tag),
    );
  };

  const handleSubmit = async (publish: boolean = false) => {
    if (!id) return;
    if (!formData.title.trim()) {
      showError("Course title is required");
      return;
    }
    if (!formData.shortDescription.trim()) {
      showError("Short description is required");
      return;
    }
    if (!formData.description.trim()) {
      showError("Course description is required");
      return;
    }
    if (!formData.category) {
      showError("Please select a category");
      return;
    }
    setLoading(true);
    try {
      const courseData = {
        ...formData,
        whatYouWillLearn: formData.whatYouWillLearn
          .filter((item) => item.trim())
          .join("\n"),
        requirements: formData.requirements
          .filter((item) => item.trim())
          .join("\n"),
        isPublished: publish,
      };
      const response = await coursesApi.updateCourse(id, courseData);
      if (response.success) {
        showSuccess(
          publish ? "Course published successfully!" : "Course updated!",
        );
        navigate(`/dashboard/instructor/courses/${id}`);
      }
    } catch (err: any) {
      showError(err.message || "Failed to update course");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (
      !confirm(
        "Are you sure you want to delete this course? This action cannot be undone.",
      )
    )
      return;
    setLoading(true);
    try {
      const response = await coursesApi.deleteCourse(id);
      if (response.success) {
        showSuccess("Course deleted successfully");
        navigate("/dashboard/instructor/courses");
      }
    } catch (err: any) {
      showError(err.message || "Failed to delete course");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        <div className="flex flex-col gap-3 mb-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/dashboard/instructor/courses`)}
              className="p-1.5 rounded-lg hover:bg-gray-100 md:p-2"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600 md:w-5 md:h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-black md:text-xl">
                Edit Course
              </h1>
              <p className="flex flex-wrap items-center gap-2 mt-0.5 text-[10px] text-gray-500 md:text-xs">
                {formData.isPublished ? (
                  <span className="px-2 py-0.5 text-[9px] font-medium text-green-700 bg-green-100 rounded-full md:text-[10px]">
                    Published
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-[9px] font-medium text-yellow-700 bg-yellow-100 rounded-full md:text-[10px]">
                    Draft
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Action Buttons - Wraps on mobile */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => navigate(`/course/${id}`)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 md:text-xs md:px-3 md:py-2"
            >
              <Eye className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span>Preview</span>
            </button>
            <button
              onClick={() =>
                navigate(`/dashboard/instructor/courses/${id}/modules`)
              }
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 md:text-xs md:px-3 md:py-2"
            >
              <BookOpen className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span>Curriculum</span>
            </button>
            <button
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 md:text-xs md:px-3 md:py-2"
            >
              <Save className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span>Save</span>
            </button>
            {!formData.isPublished && (
              <button
                onClick={() => handleSubmit(true)}
                disabled={loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md disabled:opacity-50 md:text-xs md:px-3 md:py-2"
              >
                <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>Publish</span>
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          {/* Basic Info */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <BookOpen className="w-4 h-4 text-orange-500" />
              Basic Information
            </h2>
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Course Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  placeholder="e.g., Complete Web Development Bootcamp 2024"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Short Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.shortDescription}
                  onChange={(e) =>
                    updateField("shortDescription", e.target.value)
                  }
                  placeholder="A brief summary of your course (max 200 characters)"
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
                <p className="mt-1 text-[9px] md:text-xs text-gray-400">
                  {formData.shortDescription.length}/200 characters
                </p>
              </div>
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Full Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder="Detailed description of your course"
                  rows={5}
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => updateField("category", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Sub Category
                  </label>
                  <input
                    type="text"
                    value={formData.subCategory}
                    onChange={(e) => updateField("subCategory", e.target.value)}
                    placeholder="e.g., Full Stack"
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Difficulty
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => updateField("difficulty", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    {difficulties.map((diff) => (
                      <option key={diff} value={diff}>
                        {diff.charAt(0).toUpperCase() + diff.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Language
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => updateField("language", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    {languages.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => updateField("currency", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    <option value="XAF">XAF</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <DollarSign className="w-4 h-4 text-orange-500" />
              Pricing
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Price
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => updateField("price", Number(e.target.value))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Original Price
                </label>
                <input
                  type="number"
                  value={formData.originalPrice}
                  onChange={(e) =>
                    updateField("originalPrice", Number(e.target.value))
                  }
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Discount (%)
                </label>
                <input
                  type="number"
                  value={formData.discount}
                  onChange={(e) =>
                    updateField("discount", Number(e.target.value))
                  }
                  placeholder="0"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
            </div>
          </div>

          {/* Media - Thumbnail Section - Fixed for Mobile */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <Image className="w-4 h-4 text-orange-500" />
              Media
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-[11px] md:text-sm font-medium text-gray-700">
                  Course Thumbnail
                </label>

                {/* Mobile: Stack vertically, Desktop: Row layout */}
                <div className="flex flex-col gap-3">
                  {/* Upload from Device Button - Full width on mobile */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleThumbnailUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-[11px] text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md disabled:opacity-50 sm:text-sm sm:px-4 sm:py-2 sm:w-auto"
                  >
                    {uploadingThumbnail ? (
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ImagePlus className="w-3.5 h-3.5" />
                    )}
                    Upload from Device
                  </button>

                  {/* Divider with "or" - Mobile friendly */}
                  <div className="relative flex items-center justify-center">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative px-3 text-[9px] text-gray-400 bg-white sm:text-xs">
                      OR
                    </div>
                  </div>

                  {/* URL Input - Full width on mobile */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] text-gray-500 sm:text-xs">
                      Or paste image URL
                    </label>
                    <input
                      type="url"
                      value={formData.thumbnailUrl}
                      onChange={(e) => handleThumbnailUrlChange(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Preview */}
                {formData.thumbnailUrl && (
                  <div className="mt-4">
                    <p className="mb-1 text-[9px] font-medium text-gray-600 sm:text-xs">
                      Preview:
                    </p>
                    <div className="w-40 overflow-hidden bg-gray-100 border border-gray-200 rounded-lg h-24 sm:w-48 sm:h-28">
                      <img
                        src={formData.thumbnailUrl}
                        alt="Thumbnail preview"
                        className="object-cover w-full h-full"
                        onError={(e) =>
                          ((e.target as HTMLImageElement).style.display =
                            "none")
                        }
                      />
                    </div>
                  </div>
                )}
                <p className="mt-2 text-[9px] text-gray-400 sm:text-xs">
                  Recommended size: 1280x720px. Max file size: 5MB
                </p>
              </div>

              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Promo Video URL
                </label>
                <input
                  type="url"
                  value={formData.promoVideo}
                  onChange={(e) => updateField("promoVideo", e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
            </div>
          </div>

          {/* What you'll learn */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <CheckCircle className="w-4 h-4 text-orange-500" />
              What You'll Learn
            </h2>
            <div className="space-y-2">
              {formData.whatYouWillLearn.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) =>
                      handleArrayField(
                        "whatYouWillLearn",
                        index,
                        e.target.value,
                      )
                    }
                    placeholder={`Learning point ${index + 1}`}
                    className="flex-1 px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                  {formData.whatYouWillLearn.length > 1 && (
                    <button
                      onClick={() => removeArrayItem("whatYouWillLearn", index)}
                      className="p-2 text-red-500 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem("whatYouWillLearn")}
                className="flex items-center gap-1 text-[11px] md:text-sm text-orange-600 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add more
              </button>
            </div>
          </div>

          {/* Requirements */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <Layout className="w-4 h-4 text-orange-500" />
              Requirements
            </h2>
            <div className="space-y-2">
              {formData.requirements.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) =>
                      handleArrayField("requirements", index, e.target.value)
                    }
                    placeholder={`Requirement ${index + 1}`}
                    className="flex-1 px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                  {formData.requirements.length > 1 && (
                    <button
                      onClick={() => removeArrayItem("requirements", index)}
                      className="p-2 text-red-500 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem("requirements")}
                className="flex items-center gap-1 text-[11px] md:text-sm text-orange-600 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add more
              </button>
            </div>
          </div>

          {/* Additional Information */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <Tag className="w-4 h-4 text-orange-500" />
              Additional Information
            </h2>
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Prerequisites
                </label>
                <textarea
                  value={formData.prerequisites}
                  onChange={(e) => updateField("prerequisites", e.target.value)}
                  placeholder="What students should know before taking this course"
                  rows={2}
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Tags
                </label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addTag()}
                    placeholder="Add a tag"
                    className="flex-1 px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                  <button
                    onClick={addTag}
                    type="button"
                    className="px-3 py-2 text-[11px] md:text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="flex items-center gap-1 px-2 py-1 text-[9px] md:text-xs rounded-full bg-orange-100 text-orange-700"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:text-red-500"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.mobileAccess}
                    onChange={(e) =>
                      updateField("mobileAccess", e.target.checked)
                    }
                    className="w-3.5 h-3.5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-[11px] md:text-sm text-gray-600">
                    Mobile access
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="p-4 bg-white border border-red-100 rounded-xl shadow-sm md:p-6">
            <h2 className="text-sm font-bold text-red-600 mb-3 md:mb-4">
              Danger Zone
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[11px] md:text-sm text-gray-600">
                  Delete this course
                </p>
                <p className="text-[9px] md:text-xs text-gray-400">
                  This action cannot be undone
                </p>
              </div>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-3 py-1.5 text-[10px] text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 md:text-sm md:px-4 md:py-2"
              >
                Delete Course
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
